import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { uuidv4 } from "../../../handlers/uuid";
import Toaster, { ToastLevel } from "./Toaster";

interface IToaster {
	uuid: string;
	message: string;
	level: ToastLevel;
	timeout?: number;
}

interface ToastProviderProps {
	children: ReactNode;
}

let addToastFunction: ((uuid: string, message: string, level: ToastLevel, timeout?: number) => void) | null = null;
let removeToastFunction: ((uuid: string) => void) | null = null;

export const toast = {
	info: (message: string, timeout?: number, uuid?: string): string | null => {
		if (uuid) {
			removeToastById(uuid);
		}
		return triggerToast(uuid ? uuid : uuidv4(), message, "info", timeout);
	},
	success: (message: string, timeout?: number, uuid?: string): string | null => {
		if (uuid) {
			removeToastById(uuid);
		}
		return triggerToast(uuid ? uuid : uuidv4(), message, "success", timeout);
	},
	warn: (message: string, timeout?: number, uuid?: string): string | null => {
		if (uuid) {
			removeToastById(uuid);
		}
		return triggerToast(uuid ? uuid : uuidv4(), message, "warn", timeout);
	},
	error: (message: string, timeout?: number, uuid?: string): string | null => {
		if (uuid) {
			removeToastById(uuid);
		}
		return triggerToast(uuid ? uuid : uuidv4(), message, "error", timeout);
	},
	remove: (uuid: string): void => removeToastById(uuid),
};

const triggerToast = (uuid: string, message: string, level: ToastLevel = "info", timeout = 4000): string | null => {
	if (addToastFunction) {
		addToastFunction(uuid, message, level, timeout);
		return uuid;
	}
	return null;
};

export const removeToastById = (uuid: string): void => {
	if (removeToastFunction) {
		removeToastFunction(uuid);
	}
};

const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
	const [toasts, setToasts] = useState<IToaster[]>([]);

	const addToast = useCallback((uuid: string, message: string, level: ToastLevel, timeout?: number) => {
		setToasts((currentToasts) => [...currentToasts, { uuid, message, level, timeout }]);
	}, []);

	const removeToast = useCallback((uuid: string) => {
		setToasts((currentToasts) => currentToasts.filter((toast) => toast.uuid !== uuid));
	}, []);

	useEffect(() => {
		addToastFunction = addToast;
		removeToastFunction = removeToast;
		return () => {
			addToastFunction = null;
			removeToastFunction = null;
		};
	}, [addToast, removeToast]);

	return (
		<React.Fragment>
			{children}
			<div id="zeero-toasters-container">
				{toasts.map(({ uuid, message, level, timeout }) => (
					<Toaster
						key={uuid}
						uuid={uuid}
						message={message}
						level={level}
						timeout={timeout}
						onRemove={removeToast}
					/>
				))}
			</div>
		</React.Fragment>
	);
};

export default ToastProvider;
